import { createAsyncThunk } from '@reduxjs/toolkit';


import {
  getTeamMembers,
  createTeamMember,
  reactivateTeamMember,
  updateTeamMember,
  updateTeamMemberStatus,
  deleteTeamMember,
} from '../api/team.api';

import type { OwnershipAssignment } from '../api/team.api';
import { startLoading, stopLoading } from '@/store/loaderSlice';

/* =========================
   TYPES
========================= */

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role?: string;
  designation?: string;
  department?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface GetTeamParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  department?: string;
  [key: string]: any;
}

interface CreateTeamMemberPayload {
  [key: string]: any;
}

interface UpdateTeamMemberPayload {
  memberId: string;
  updates: Record<string, any>;
}

interface UpdateTeamMemberStatusPayload {
  memberId: string;
  status: string;
}

interface ReactivateTeamMemberPayload {
  memberId: string;
  name: string;
  password: string;
  requestedHrAdminId?: string;
}

interface TeamApiError {
  code?: number;
  message: string;
  errorCode?: string;
  data?: {
    memberId?: string;
    name?: string;
    email?: string;
    requestedHrAdminId?: string;
    [key: string]: any;
  };
}

export const fetchTeamMembersThunk = createAsyncThunk<
  any,
  (GetTeamParams & { force?: boolean }) | undefined,
  {
    rejectValue: string;
    state: {
      team: {
        loading: boolean;
        hasLoaded: boolean;
      };
    };
  }
>(
  'team/fetchTeamMembers',
  async (params = {}, { rejectWithValue }) => {
    try {
      // `force` sirf Redux cache control ke liye hai.
      // Ise API query params mein send nahi karna.
      const { force: _force, ...queryParams } = params;

      return await getTeamMembers(queryParams);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to fetch team members'
      );
    }
  },
  {
    condition: (params, { getState }) => {
      const teamState = getState().team;
      const force = params?.force === true;

      // Running request ke dauran duplicate call block karein.
      if (teamState.loading) {
        return false;
      }

      // Normal calls: successful fetch ke baad cached data reuse karein.
      if (teamState.hasLoaded && !force) {
        return false;
      }

      // First fetch ya explicit refresh.
      return true;
    },
  }
);


export const createTeamMemberThunk = createAsyncThunk<
  any,
  CreateTeamMemberPayload,
  { rejectValue: TeamApiError }
>(
  'team/createTeamMember',
  async (member, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());

    try {
      return await createTeamMember(member);
    } catch (error: any) {
      const responseData = error?.response?.data || error;

      return rejectWithValue({
        code: responseData?.code,
        message: responseData?.message || 'Failed to create team member',
        errorCode: responseData?.errorCode,
        data: responseData?.data,
      });
    } finally {
      dispatch(stopLoading());
    }
  }
);


export const reactivateTeamMemberThunk = createAsyncThunk<
  any,
  ReactivateTeamMemberPayload,
  { rejectValue: TeamApiError }
>(
  'team/reactivateTeamMember',
  async ({ memberId, name, password, requestedHrAdminId }, { rejectWithValue }) => {
    try {
      return await reactivateTeamMember(memberId, {
        name,
        password,
        requestedHrAdminId,
      });
    } catch (error: any) {
      const responseData = error?.response?.data || error;
      return rejectWithValue({
        code: responseData?.code,
        message: responseData?.message || 'Failed to reactivate team member',
        errorCode: responseData?.errorCode,
        data: responseData?.data,
      });
    }
  }
);

export const updateTeamMemberThunk = createAsyncThunk<
  any,
  UpdateTeamMemberPayload,
  { rejectValue: string }
>(
  'team/updateTeamMember',
  async ({ memberId, updates }, { dispatch, rejectWithValue }) => {
    dispatch(startLoading());

    try {
      return await updateTeamMember(memberId, updates);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update team member'
      );
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateTeamMemberStatusThunk = createAsyncThunk<
  any,
  UpdateTeamMemberStatusPayload,
  { rejectValue: string }
>(
  'team/updateTeamMemberStatus',
  async ({ memberId, status }, { rejectWithValue }) => {
    try {
      return await updateTeamMemberStatus(memberId, status);
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Failed to update member status'
      );
    }
  }
);




/* =========================
   DELETE TEAM MEMBER
========================= */

interface DeleteTeamMemberPayload {
  memberId: string;
  ownershipAssignments: OwnershipAssignment[];
}

export const deleteTeamMemberThunk =
  createAsyncThunk<
    string,
    DeleteTeamMemberPayload,
    { rejectValue: string }
  >(
    'team/deleteTeamMember',
    async (
      { memberId, ownershipAssignments },
      { rejectWithValue }
    ) => {
      try {
        await deleteTeamMember(
          memberId,
          ownershipAssignments
        );

        // Redux ko deleted member ki ID return karenge.
        return memberId;
      } catch (error: any) {
        const responseData =
          error?.response?.data || error;

        return rejectWithValue(
          responseData?.message ||
          error?.message ||
          'Failed to delete team member'
        );
      }
    }
  );
